<template>
  <div>
    <v-expansion-panels flat multiline>
      <v-expansion-panel>
        <v-expansion-panel-header :id="`dat-instance-panel-${instance.eid}`">
          <v-col class="instance-attribute version-container-info col-3">
            <span class="attribute-header">{{ $t(`map.tolldomains.dsrc_instances.headers.instance_name`) }}<br></span>
            <div :id="`instance-${instance.eid}-name`" class="attribute-header-value">{{ instance.name }}</div>
          </v-col>
          <v-col class="instance-attribute col-1">
            <span class="attribute-header">{{ $t(`map.tolldomains.dsrc_instances.headers.eid`) }}<br></span>
            <div :id="`instance-${instance.eid}-eid`" class="attribute-header-value">{{ instance.eid }}</div>
          </v-col>
          <v-col class="instance-attribute col-2">
            <v-chip
              :id="`instance-${instance.eid}-state`"
              dark
              small
              class="mr-9 ml-4 version-archive-requested"
            >
              {{ $t(`map.tolldomains.dsrc_instances.states.${getInstanceStatus(instance).state}`) }}
            </v-chip>
          </v-col>
          <v-col class="instance-attribute col-2">
            <v-tooltip
              v-if="instance.activeRules && instance.activeRules.length > 0"
              bottom
            >
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <span :class="`dot-${getInstanceStatus(instance).flag}`">&nbsp;</span>
                  <span v-if="instance.activeRules.length > 1" :id="`instance-${instance.eid}-usedstate`" class="attribute-header-value">
                    {{ $tc(`map.tolldomains.dsrc_instances.states.used_in`, instance.activeRules.length, { count: instance.activeRules.length }) }}
                  </span>
                  <span v-else :id="`instance-${instance.eid}-usedstate`" class="attribute-header-value">{{ $tc(`map.tolldomains.dsrc_instances.states.used_in`, 1) }}</span>
                </div>
              </template>
              <div class="font-weight-bold">{{ $t(`map.tolldomains.dsrc_instances.states.used`) }}</div>
              <div v-for="rule in instance.activeRules" :key="rule.id">
                <span :id="`instance-${instance.eid}-usedin`">{{ rule.id }} - {{ rule.tollDomain.name }}</span>
              </div>
            </v-tooltip>
            <div v-else>
              <span :class="`dot-${getInstanceStatus(instance).flag}`">&nbsp;</span>
              <span :id="`instance-${instance.eid}-usedstate`" class="attribute-header-value">{{ $t(`map.tolldomains.dsrc_instances.states.${getInstanceStatus(instance).flag}`) }}</span>
            </div>
          </v-col>
          <v-col v-if="instance.deleted" class="instance-attribute">
            <span class="attribute-header">{{ $t(`map.tolldomains.dsrc_instances.headers.valid_to`) }}<br></span>
            <span :id="`instance-${instance.eid}-validto`" class="attribute-header-value">{{ instance.deleted && instance.deletedAt ? formatDate(instance.deletedAt) : '-' }}</span>
          </v-col>
          <v-col v-else class="instance-attribute" />
          <v-col class="instance-attribute action-container col-1">
            <v-icon
              v-if="canBeArchived()"
              :id="`instance-${instance.eid}-archive`"
              :title="$t('common.archive')"
              class="mx-2 btn-instance"
              dark
              @click.stop="archive(instance)"
            >
              mdi-archive-arrow-down-outline
            </v-icon>
            <div v-else class="no-btn-instance" />
            <v-icon
              v-if="canBeDeleted()"
              :id="`instance-${instance.eid}-delete`"
              :title="$t('common.delete')"
              class="mx-2"
              dark
              @click.stop="deleteContainer(instance)"
            >
              mdi-trash-can-outline
            </v-icon>
            <div v-else class="no-btn-instance" />
          </v-col>
          <template v-slot:actions>
            <v-icon id="version-expand-button" color="#d1d1d1"> $expand </v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col
              v-for="legend in legends"
              :key="legend"
              class="list-legend"
            >
              {{ $t(legend) }}
            </v-col>
          </v-row>
          <v-row
            align="center"
            class="instance-versions"
          >
            <v-col :id="`instance-${instance.eid}-dat-name`" :title="instance.dat ? instance.dat.name : ''">{{ instance.dat ? instance.dat.uid : '-' }}</v-col>
            <v-col :id="`instance-${instance.eid}-dat-transacsec`">{{ instance.transactionSecurity.toUpperCase() }}</v-col>
            <v-col :id="`instance-${instance.eid}-dat-contextmark`">{{ instance.contextMarkPriority }}</v-col>
            <v-col :id="`instance-${instance.eid}-dat-forcedreturn`">{{ instance.forcedReturnStatus.toUpperCase() }}</v-col>
            <v-col :id="`instance-${instance.eid}-dat-vst`">{{ instance.vst.toUpperCase() }}</v-col>
            <v-col
              :id="`instance-${instance.eid}-dat-onban`"
              :title="!getCurrentUserIsAbleToWrite('tolling.tollDomain') || instance.usedInCurrentOrFuturRule ? $t('map.tolldomains.dsrc_instances.instance_in_use') : ''"
            >
              <div>{{ getBanActivatedLabel(instance.banActivated) }}</div>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <ConfirmDialog
      :headline="confirmData.headline"
      :text="confirmData.text"
      :plurality="0"
      :actions="confirmData.actions"
      :initialDisplayState="confirmation"
      @modal="confirmation = false"
    />
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import ConfirmDialog from '@/components/common/ConfirmDialog/ConfirmDialog.vue';
import Utils from '@/mixins/Utils';
import { DatInstanceTypeEnum, InstanceRelated } from 'ctp-toll-domain';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { INotification, store as instanceStore } from '@/store/modules/datInstances/types';
import { store as userPermissionStore } from '@/store/modules/permissions/types';

@Component({
  components: {
    ConfirmDialog
  }
})
export default class VersionPanelContainer extends Mixins(Utils) {
    @Prop()
    private masterAccount!: MasterAccountInfo;

    @Prop()
    private instance!: InstanceRelated;

    @instanceStore.Getter
    private getInstanceStatus!: (instance: InstanceRelated) => any;

    @instanceStore.Action
    private archiveDeleteDatInstance!: (payload: any) => Promise<INotification>;

    @userPermissionStore.Getter
    private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

    private legends = [
      'map.tolldomains.dsrc_instances.headers.dat_associated',
      'map.tolldomains.dsrc_instances.transaction_security',
      'map.tolldomains.dsrc_instances.contextMarkPriority',
      'map.tolldomains.dsrc_instances.forced_return_status',
      'map.tolldomains.dsrc_instances.vst_security_level',
      'map.tolldomains.dsrc_instances.headers.ban_activated'
    ];

    private confirmation = false;

    private confirmData = {
      headline: 'common.archive',
      text: 'map.tolldomains.dsrc_instances.confirm_delete',
      actions: {
        continue: {
          text: 'common.continue', caller: this.handleArchiveInstance
        },
        cancel: {
          text: 'common.cancel', caller: this.onActionCancel
        }
      }
    };

    private archive() {
      this.confirmData = {
        headline: 'common.archive',
        text: 'map.tolldomains.dsrc_instances.confirm_archive',
        actions: {
          continue: {
            text: 'common.confirm', caller: this.handleArchiveInstance
          },
          cancel: {
            text: 'common.cancel', caller: this.onActionCancel
          }
        }
      };
      this.confirmation = true;
    }

    private async deleteContainer() {
      this.confirmData = {
        headline: 'common.delete',
        text: 'map.tolldomains.dsrc_instances.confirm_delete',
        actions: {
          continue: {
            text: 'common.confirm', caller: this.handleDeleteInstance
          },
          cancel: {
            text: 'common.cancel', caller: this.onActionCancel
          }
        }
      };
      this.confirmation = true;
    }

    private async handleArchiveInstance() {
      await this.archiveDeleteDatInstance({ masterId: this.instance.masterId, eid: this.instance.eid, archiving: true });
      this.$emit('updateList');
      this.confirmation = false;
    }

    private async handleDeleteInstance() {
      await this.archiveDeleteDatInstance({ masterId: this.instance.masterId, eid: this.instance.eid, archiving: false });
      this.$emit('updateList');
      this.confirmation = false;
    }

    private onActionCancel() {
      this.confirmation = false;
    }

    private getCurrentUserIsAbleToWrite(featureName: string) {
      return featureName ? this.getCurrentUserIsAbleTo('write', featureName) : false;
    }

    private getBanActivatedLabel(banActivated: boolean) {
      return banActivated ? 'ON' : 'OFF';
    }

    private canBeDeleted() {
      return this.getInstanceStatus(this.instance).state === 'inactive'
      && this.getInstanceStatus(this.instance).flag === 'unused' && this.instance.type !== DatInstanceTypeEnum.Uni && this.getCurrentUserIsAbleToWrite('dsrcInstance.write');
    }

    private canBeArchived() {
      return this.getInstanceStatus(this.instance).state === 'active'
      && this.getInstanceStatus(this.instance).flag === 'unused';
    }
}
</script>

<style lang="scss" scoped>
@import './styles/version-panel-container.scss';
</style>
